/**
 * @file useUserHistory.js
 * @description 观看历史列表
 * @module features/user/composables/useUserHistory
 */

import {
  computed,
  nextTick,
  onActivated,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { convertRouteParams } from '@/shared/utils/idHelper'
import { isMobileLayoutViewport } from '@/shared/utils/mobileLayout'
import { getOssImageUrl } from '@/shared/utils/ossImage'
import { fetchUserHistoryPageApi } from '@/features/user/api/userApi'

export function useUserHistory() {
  const router = useRouter()
  const route = useRoute()
  const historyList = ref([])
  const accumulatedHistory = ref([])
  const currentPage = ref(1)
  const _pageSizePc = 10
  const _pageSizeMobile = 6
  const pageSize = ref(10)
  const totalItems = ref(0)
  const totalPages = ref(0)
  const token = ref('')
  const isMobile = ref(false)
  const loading = ref(false)
  const loadingMore = ref(false)
  const historySentinel = ref(null)
  let infiniteObserver = null

  const displayHistory = computed(() =>
    isMobile.value ? accumulatedHistory.value : historyList.value
  )
  const historyNoMore = computed(() => {
    if (!isMobile.value || totalItems.value === 0) return true
    return accumulatedHistory.value.length >= totalItems.value
  })
  const showInfiniteEndTip = computed(() => {
    const ps = pageSize.value
    if (totalItems.value <= ps) return false
    if (loading.value || loadingMore.value) return false
    if (displayHistory.value.length === 0) return false
    if (isMobile.value) {
      return historyNoMore.value
    }
    return currentPage.value >= totalPages.value && totalPages.value >= 1
  })

  function updateIsMobile() {
    isMobile.value = isMobileLayoutViewport()
    pageSize.value = isMobile.value ? _pageSizeMobile : _pageSizePc
  }

  function unbindInfiniteObserver() {
    if (infiniteObserver) {
      infiniteObserver.disconnect()
      infiniteObserver = null
    }
  }

  function bindInfiniteObserver() {
    unbindInfiniteObserver()
    if (!isMobile.value || typeof IntersectionObserver === 'undefined') return
    const el = historySentinel.value
    if (!el || historyNoMore.value || loading.value || loadingMore.value) return
    infiniteObserver = new IntersectionObserver(
      (entries) => {
        if (
          !entries[0]?.isIntersecting ||
          historyNoMore.value ||
          loadingMore.value ||
          loading.value ||
          !isMobile.value
        ) {
          return
        }
        fetchReadingHistory(true)
      },
      { root: null, rootMargin: '120px', threshold: 0 }
    )
    infiniteObserver.observe(el)
  }

  function resolveRequestPageNum(append) {
    if (append) return currentPage.value + 1
    if (isMobile.value) return 1
    return currentPage.value
  }

  function mapHistoryItem(item) {
    return {
      id: item.id,
      name: item.name,
      cover: getOssImageUrl(item.cover, 'thumb'),
      synop: item.synop,
      progressTotal: item.progressTotal,
      themes: item.comicThemeVos
        ? item.comicThemeVos.map((theme) => theme.themeName)
        : [],
    }
  }

  async function fetchReadingHistory(append) {
    if (append) {
      if (
        !isMobile.value ||
        loadingMore.value ||
        historyNoMore.value ||
        accumulatedHistory.value.length >= totalItems.value
      ) {
        return
      }
    }
    if (!token.value) {
      console.warn('未找到用户授权信息，请登录后重试')
      return
    }
    const pageNum = resolveRequestPageNum(append)
    if (!append) {
      loading.value = true
    } else {
      loadingMore.value = true
    }
    try {
      const response = await fetchUserHistoryPageApi({
        pageNum,
        pageSize: pageSize.value,
      })

      if (response.data.code === 200 || response.data.code === 0) {
        const { contents, total } = response.data
        const list = Array.isArray(contents) ? contents : []
        const mapped = list.map(mapHistoryItem)
        totalItems.value = total || 0
        totalPages.value = Math.max(
          1,
          Math.ceil(totalItems.value / pageSize.value)
        )

        if (!append) {
          historyList.value = mapped
          if (isMobile.value) {
            accumulatedHistory.value = [...mapped]
            currentPage.value = 1
          } else {
            accumulatedHistory.value = []
          }
        } else if (mapped.length === 0) {
          currentPage.value = pageNum
        } else {
          accumulatedHistory.value.push(...mapped)
          currentPage.value = pageNum
        }
      } else {
        console.error('获取观看历史失败:', response.data.msg)
      }
    } catch (error) {
      console.error('请求错误:', error)
    } finally {
      loading.value = false
      loadingMore.value = false
      nextTick(() => bindInfiniteObserver())
    }
  }

  function continueReading(history) {
    router.push({
      name: 'ComicDetail',
      params: convertRouteParams({ id: history.id }),
    })
  }

  function formatProgress(progressTotal) {
    return (progressTotal * 100).toFixed(0) + '%'
  }

  function nextPage() {
    if (isMobile.value) return
    if (currentPage.value < totalPages.value) {
      currentPage.value++
      fetchReadingHistory(false)
    }
  }

  function previousPage() {
    if (isMobile.value) return
    if (currentPage.value > 1) {
      currentPage.value--
      fetchReadingHistory(false)
    }
  }

  function onResize() {
    const prev = isMobile.value
    updateIsMobile()
    if (prev !== isMobile.value) {
      currentPage.value = 1
      accumulatedHistory.value = []
      fetchReadingHistory(false)
    }
  }

  function resetAndFetch() {
    currentPage.value = 1
    accumulatedHistory.value = []
    fetchReadingHistory(false)
  }

  watch(
    () => ({ name: route.name, module: route.params.module }),
    (to, from) => {
      if (
        to.name === 'UserModule' &&
        to.module === 'UserHistory' &&
        from?.name === 'UserModule' &&
        from?.module !== 'UserHistory'
      ) {
        resetAndFetch()
      }
    }
  )

  onMounted(() => {
    token.value = JSON.parse(localStorage.getItem('user'))?.token || ''
    if (!token.value) {
      console.warn('未找到用户授权信息，请登录后重试')
    }
    updateIsMobile()
    window.addEventListener('resize', onResize)
    fetchReadingHistory(false)
    nextTick(() => bindInfiniteObserver())
  })

  onBeforeUnmount(() => {
    window.removeEventListener('resize', onResize)
    unbindInfiniteObserver()
  })

  onActivated(() => {
    resetAndFetch()
  })

  return {
    loading,
    loadingMore,
    isMobile,
    historySentinel,
    displayHistory,
    showInfiniteEndTip,
    currentPage,
    totalPages,
    totalItems,
    continueReading,
    formatProgress,
    nextPage,
    previousPage,
  }
}
